import { motion } from "framer-motion";
import { Star, ArrowUpRight, Clock } from "@phosphor-icons/react";

export default function ExclusiveToMyLocal({ businesses = [] }) {
  if (!businesses.length) return null;

  return (
    <section
      id="exclusive"
      data-testid="exclusive-section"
      className="relative bg-[#0A0A0A] text-white overflow-hidden py-20 md:py-28"
    >
      <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full brand-gradient opacity-20 blur-3xl" />
      <div className="absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full brand-gradient opacity-10 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-14">
          <div className="max-w-2xl">
            <p className="overline text-[#FF1F8B] mb-4 flex items-center gap-2">
              <Star size={14} weight="fill" />
              Only on My Local
            </p>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Local <span className="gradient-text">spots.</span>
            </h2>
            <p className="mt-5 text-zinc-400 max-w-lg leading-relaxed">
              Order direct. Money stays in the area.
            </p>
          </div>
          <a
            href="https://www.mylocalapp.shop/"
            target="_blank"
            rel="noreferrer"
            data-testid="see-all-exclusive"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:gap-3 transition-all"
          >
            Find your local
            <ArrowUpRight size={16} weight="bold" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {businesses.map((b, i) => (
            <motion.a
              key={b.id}
              href="https://www.mylocalapp.shop/"
              target="_blank"
              rel="noreferrer"
              data-testid={`exclusive-card-${b.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="card-hover group relative block bg-white/[0.04] rounded-3xl overflow-hidden border border-white/10 hover:-translate-y-1 hover:bg-white/[0.06] transition-all duration-500"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-900">
                <img
                  src={b.image_url}
                  alt={b.name}
                  loading="lazy"
                  className="card-img absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full brand-gradient text-white shadow-lg shadow-[#FF1F8B]/30">
                    <Star size={10} weight="fill" />
                    Exclusive
                  </span>
                </div>
              </div>
              <div className="p-6 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display font-extrabold text-xl tracking-tight">
                    {b.name}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1 flex items-center gap-3">
                    <span>{b.category}</span>
                    <span className="inline-flex items-center gap-1 text-zinc-500">
                      <Clock size={12} weight="duotone" />
                      {b.eta_mins || "45-60 min"}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-1 text-sm font-semibold text-white shrink-0">
                  <Star size={14} weight="fill" className="text-[#FF1F8B]" />
                  {b.rating?.toFixed(1) ?? "4.8"}
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
